import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronDown, Ruler, FileText, Trash2, Palette, Pencil, Link2 as LinkIcon } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Modal,
  Field,
  LoadingState,
  ErrorState,
  EmptyState,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { ITEM_CATEGORIES } from '@/lib/constants';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { findCloseMatch, normalizeName } from '@/lib/match';
import type { Designer, DesignerSize } from '@/lib/types';

const splitList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const ensureHttp = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

// Aggregate (brand-derived) rows arrive with id === null; curated rows have a real id.
type DesignerRow = Omit<Designer, 'id'> & {
  id: string | null;
  spendInCloset?: number;
  spendPending?: number;
  spendWishlist?: number;
  spendRemoved?: number;
  countInCloset?: number;
  countPending?: number;
  countWishlist?: number;
  countRemoved?: number;
  lastPurchase?: string | null;
};

// Link to the items list filtered to this designer (brand) + status, newest first.
function designerItemsLink(brand: string, status: 'owned' | 'wishlist' | 'archived'): string {
  const params = new URLSearchParams({ brand, status, sort: 'acquired', order: 'desc' });
  return `/wardrobe/items?${params.toString()}`;
}

// Inline link affordance after a name — a click-stopping span so it nests
// safely inside the row's toggle button.
function WebsiteLink({ url }: { url: string }) {
  const href = ensureHttp(url);
  const open = () => window.open(href, '_blank', 'noopener,noreferrer');
  return (
    <span
      role="link"
      tabIndex={0}
      title="Open website"
      onClick={(e) => { e.stopPropagation(); open(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); open(); } }}
      className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
    >
      <LinkIcon className="w-3.5 h-3.5" />
    </span>
  );
}

function SpendLink({ label, amount, count, to }: { label: string; amount: number; count?: number; to: string }) {
  return (
    <Link to={to} className="flex items-center justify-between py-2 group">
      <span className="text-sm text-foreground">
        {label}
        {count != null && (
          <span className="text-xs text-muted-foreground"> · {count} {count === 1 ? 'item' : 'items'}</span>
        )}
      </span>
      <span className="text-sm font-semibold text-primary group-hover:underline">
        {formatCurrency(amount)}
      </span>
    </Link>
  );
}

const FIT_OPTIONS: { value: NonNullable<DesignerSize['fit']>; label: string }[] = [
  { value: 'true-to-size', label: 'True to Size' },
  { value: 'runs-small', label: 'Runs Small' },
  { value: 'runs-large', label: 'Runs Large' },
];

function fitLabel(fit: DesignerSize['fit']): string {
  return FIT_OPTIONS.find((f) => f.value === fit)?.label ?? 'True to Size';
}

function fitColor(fit: DesignerSize['fit']): string {
  switch (fit) {
    case 'runs-small':
      return 'text-amber-600 bg-amber-50';
    case 'runs-large':
      return 'text-blue-600 bg-blue-50';
    default:
      return 'text-green-600 bg-green-50';
  }
}

// Aggregate (brand-derived) rows arrive with id === null. Only curated rows
// (real id) can be edited or deleted.
function hasRealId(designer: DesignerRow): designer is DesignerRow & { id: string } {
  return typeof designer.id === 'string' && designer.id.length > 0;
}

export function DesignersPage() {
  const { data, loading, error, refetch } = useApi<{ designers: DesignerRow[] }>('/designers');
  const { mutate, loading: saving } = useMutation();

  const designers = data?.designers ?? [];
  const totalItems = designers.reduce((sum, d) => sum + (d.itemCount ?? 0), 0);
  const spend = designers.reduce(
    (acc, d) => ({
      inCloset: acc.inCloset + (d.spendInCloset ?? 0),
      pending: acc.pending + (d.spendPending ?? 0),
      wishlist: acc.wishlist + (d.spendWishlist ?? 0),
      removed: acc.removed + (d.spendRemoved ?? 0),
    }),
    { inCloset: 0, pending: 0, wishlist: 0, removed: 0 },
  );

  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Add / edit designer
  const [showDesignerModal, setShowDesignerModal] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState<(DesignerRow & { id: string }) | null>(null);
  const [designerName, setDesignerName] = useState('');
  const [designerNotes, setDesignerNotes] = useState('');
  const [designerWebsite, setDesignerWebsite] = useState('');
  const [designerAliases, setDesignerAliases] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Delete designer confirmation
  const [deletingDesigner, setDeletingDesigner] = useState<(DesignerRow & { id: string }) | null>(null);

  // Add size
  const [sizeForDesigner, setSizeForDesigner] = useState<DesignerRow | null>(null);
  const [garmentType, setGarmentType] = useState('');
  const [sizeValue, setSizeValue] = useState('');
  const [sizeFit, setSizeFit] = useState<NonNullable<DesignerSize['fit']>>('true-to-size');

  // A stable key for expansion: real id, else name (aggregate rows have id === null).
  const designerKey = (d: DesignerRow): string => (hasRealId(d) ? d.id : `name:${d.name}`);

  const openAdd = () => {
    setEditingDesigner(null);
    setDesignerName('');
    setDesignerNotes('');
    setDesignerWebsite('');
    setDesignerAliases('');
    setFormError(null);
    setShowDesignerModal(true);
  };

  const openEdit = (designer: DesignerRow & { id: string }) => {
    setEditingDesigner(designer);
    setDesignerName(designer.name);
    setDesignerNotes(designer.notes ?? '');
    setDesignerWebsite(designer.websiteUrl ?? '');
    setDesignerAliases((designer.aliases ?? []).join(', '));
    setFormError(null);
    setShowDesignerModal(true);
  };

  const handleSaveDesigner = async () => {
    if (!designerName.trim()) return;
    setFormError(null);
    if (editingDesigner) {
      // PATCH must go through api.patch directly (useMutation only does post/put/delete).
      try {
        await api.patch(`/designers/${editingDesigner.id}`, {
          name: designerName.trim(),
          notes: designerNotes.trim() || null,
          websiteUrl: designerWebsite.trim() || null,
          aliases: splitList(designerAliases),
        });
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Failed to save designer');
        return;
      }
      setShowDesignerModal(false);
      refetch();
    } else {
      const res = await mutate('post', '/designers', {
        name: designerName.trim(),
        notes: designerNotes.trim() || undefined,
        websiteUrl: designerWebsite.trim() || undefined,
        aliases: splitList(designerAliases),
      });
      if (res !== null) {
        setShowDesignerModal(false);
        refetch();
      } else {
        setFormError('Failed to add designer');
      }
    }
  };

  // When adding, surface an existing designer the typed name likely duplicates.
  const closeDesigner = !editingDesigner && designerName.trim() ? findCloseMatch(designerName.trim(), designers) : null;

  const addAliasToExisting = async (d: DesignerRow) => {
    const aliasName = designerName.trim();
    if (hasRealId(d)) {
      await api.patch(`/designers/${d.id}`, { aliases: [...(d.aliases ?? []), aliasName] });
    } else {
      await mutate('post', '/designers', { name: d.name, aliases: [aliasName] });
    }
    setShowDesignerModal(false);
    refetch();
  };

  // While editing, surface separate brand records whose name matches an alias
  // being saved — these are duplicates that can be merged in.
  const aliasNorms = splitList(designerAliases).map(normalizeName).filter(Boolean);
  const dupDesigners = editingDesigner
    ? designers.filter((d) => normalizeName(d.name) !== normalizeName(editingDesigner.name) && aliasNorms.includes(normalizeName(d.name)))
    : [];

  // Explicit "merge a duplicate brand into this one" workflow.
  const [mergeIntoD, setMergeIntoD] = useState<DesignerRow | null>(null);
  const [mergeFromName, setMergeFromName] = useState('');
  const [mergingD, setMergingD] = useState(false);

  const handleMergeDesigners = async () => {
    if (!mergeIntoD || !mergeFromName) return;
    setMergingD(true);
    try {
      await api.post('/designers/merge', { fromName: mergeFromName, intoName: mergeIntoD.name });
      setMergeIntoD(null);
      setMergeFromName('');
      setExpandedKey(null);
      refetch();
    } finally {
      setMergingD(false);
    }
  };

  const mergeDesignerInto = async (fromName: string) => {
    if (!editingDesigner) return;
    await api.patch(`/designers/${editingDesigner.id}`, {
      name: designerName.trim(),
      notes: designerNotes.trim() || null,
      websiteUrl: designerWebsite.trim() || null,
      aliases: splitList(designerAliases),
    });
    await api.post('/designers/merge', { fromName, intoName: designerName.trim() });
    setShowDesignerModal(false);
    refetch();
  };

  // Promote an aggregate (id === null) row into a curated designer so it
  // becomes editable. Creates the row from its name.
  const handleAddToMyDesigners = async (designer: DesignerRow) => {
    const res = await mutate('post', '/designers', { name: designer.name });
    if (res !== null) refetch();
  };

  const handleDeleteDesigner = async () => {
    if (!deletingDesigner) return;
    const res = await mutate('delete', `/designers/${deletingDesigner.id}`);
    if (res !== null) {
      setDeletingDesigner(null);
      setExpandedKey(null);
      refetch();
    }
  };

  const handleAddSize = async () => {
    if (!sizeForDesigner || !garmentType.trim() || !sizeValue.trim()) return;
    const res = await mutate('post', '/designer-sizes', {
      designerName: sizeForDesigner.name,
      garmentType: garmentType.trim(),
      size: sizeValue.trim(),
      fit: sizeFit,
    });
    if (res !== null) {
      setSizeForDesigner(null);
      setGarmentType('');
      setSizeValue('');
      setSizeFit('true-to-size');
      refetch();
    }
  };

  const handleDeleteSize = async (sizeId: string) => {
    const res = await mutate('delete', `/designer-sizes/${sizeId}`);
    if (res !== null) refetch();
  };

  return (
    <Page>
      <PageHeader section="My Designers" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <>
            <Card className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Spending</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(spend.inCloset)}
                    {spend.pending > 0 && (
                      <span className="text-base font-semibold text-muted-foreground">
                        {' '}
                        ({formatCurrency(spend.pending)} pending)
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Wishlist: {formatCurrency(spend.wishlist)}
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    Removed: {formatCurrency(spend.removed)}
                  </p>
                </div>
                <div className="text-right text-xs text-muted-foreground space-y-0.5 flex-shrink-0">
                  <p>{designers.length} designers</p>
                  <p>{totalItems} items</p>
                </div>
              </div>
            </Card>

            <Button fullWidth onClick={openAdd}>
              <Plus className="w-4 h-4" />
              Add Designer
            </Button>

            {designers.length === 0 ? (
              <EmptyState
                icon={<Palette className="w-8 h-8" />}
                title="No designers yet"
                description="Add designers to track sizing and items."
              />
            ) : (
              <div className="space-y-3">
                {designers.map((designer) => {
                  const key = designerKey(designer);
                  const expanded = expandedKey === key;
                  const sizes = designer.sizes ?? [];
                  const curated = hasRealId(designer);
                  return (
                    <Card key={key} className="overflow-hidden">
                      <button
                        onClick={() => setExpandedKey(expanded ? null : key)}
                        className="w-full p-4 flex items-center gap-4 text-left"
                      >
                        <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center text-lg font-bold text-muted-foreground flex-shrink-0">
                          {designer.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground truncate">{designer.name}</h3>
                            {designer.websiteUrl && <WebsiteLink url={designer.websiteUrl} />}
                          </div>
                          <p className="text-xs text-muted-foreground">{designer.itemCount ?? 0} items</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-foreground">
                            {formatCurrency(designer.spendInCloset ?? 0)}
                            {(designer.spendPending ?? 0) > 0 && (
                              <span className="text-[11px] font-medium text-muted-foreground">
                                {' '}
                                (+{formatCurrency(designer.spendPending ?? 0)})
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-muted-foreground/70">
                            Wishlist: {formatCurrency(designer.spendWishlist ?? 0)}
                          </p>
                          <p className="text-[10px] text-muted-foreground/70">
                            Removed: {formatCurrency(designer.spendRemoved ?? 0)}
                          </p>
                        </div>
                        <ChevronDown
                          className={cn('w-5 h-5 text-muted-foreground transition-transform flex-shrink-0', expanded && 'rotate-180')}
                        />
                      </button>

                      {expanded && (
                        <div className="border-t border-border">
                          {designer.notes && (
                            <div className="p-4 border-b border-border flex items-start gap-2">
                              <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                              <p className="text-sm text-foreground">{designer.notes}</p>
                            </div>
                          )}

                          {curated ? (
                            <div className="p-4 border-b border-border flex gap-2">
                              <button
                                onClick={() => openEdit(designer)}
                                className="flex-1 flex items-center justify-center gap-2 py-2 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-secondary/70 transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit Designer
                              </button>
                              <button
                                onClick={() => setDeletingDesigner(designer)}
                                className="flex items-center justify-center px-4 py-2 bg-destructive/10 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors"
                                aria-label="Delete designer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="p-4 border-b border-border">
                              <button
                                onClick={() => handleAddToMyDesigners(designer)}
                                disabled={saving}
                                className="w-full flex items-center justify-center gap-2 py-2 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-secondary/70 transition-colors disabled:opacity-50"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add to My Designers
                              </button>
                              <p className="text-[10px] text-muted-foreground text-center mt-2">
                                Derived from your items. Add it to edit notes and details.
                              </p>
                            </div>
                          )}

                          {designers.length > 1 && (
                            <div className="p-4 border-b border-border">
                              <button
                                onClick={() => { setMergeIntoD(designer); setMergeFromName(''); }}
                                className="w-full flex items-center justify-center gap-2 py-2 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-secondary/70 transition-colors"
                              >
                                <LinkIcon className="w-3.5 h-3.5" />
                                Merge a duplicate into this brand
                              </button>
                            </div>
                          )}

                          {/* Spending — each figure links to the filtered items list. */}
                          <div className="p-4 border-b border-border">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                              Spending
                            </h4>
                            <div className="divide-y divide-border">
                              <SpendLink
                                label="In Closet"
                                amount={designer.spendInCloset ?? 0}
                                count={designer.countInCloset}
                                to={designerItemsLink(designer.name, 'owned')}
                              />
                              <SpendLink
                                label="Wishlist"
                                amount={designer.spendWishlist ?? 0}
                                count={designer.countWishlist}
                                to={designerItemsLink(designer.name, 'wishlist')}
                              />
                              <SpendLink
                                label="Removed"
                                amount={designer.spendRemoved ?? 0}
                                count={designer.countRemoved}
                                to={designerItemsLink(designer.name, 'archived')}
                              />
                            </div>
                            {designer.lastPurchase && (
                              <p className="text-[10px] text-muted-foreground mt-2">
                                Last purchased {formatDate(designer.lastPurchase)}
                              </p>
                            )}
                          </div>

                          <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                <Ruler className="w-3.5 h-3.5" />
                                My Sizing
                              </h4>
                              <button
                                onClick={() => setSizeForDesigner(designer)}
                                className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add Size
                              </button>
                            </div>
                            {sizes.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-2">No sizes recorded</p>
                            ) : (
                              <div className="space-y-2">
                                {sizes.map((size) => (
                                  <div key={size.id} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <span className="text-sm text-foreground truncate">{size.garmentType}</span>
                                      <span className="px-2 py-0.5 bg-secondary rounded-full text-xs font-medium text-foreground flex-shrink-0">
                                        {size.size}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span
                                        className={cn(
                                          'px-2 py-0.5 rounded-full text-[10px] font-medium',
                                          fitColor(size.fit),
                                        )}
                                      >
                                        {fitLabel(size.fit)}
                                      </span>
                                      {size.isManual && (
                                        <button
                                          onClick={() => handleDeleteSize(size.id)}
                                          className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                                          aria-label="Delete size"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Main>

      {/* Add / Edit Designer Modal */}
      <Modal
        isOpen={showDesignerModal}
        onClose={() => setShowDesignerModal(false)}
        title={editingDesigner ? 'Edit Designer' : 'Add Designer'}
        footer={
          <Button fullWidth onClick={handleSaveDesigner} disabled={!designerName.trim() || saving}>
            {saving ? 'Saving…' : editingDesigner ? 'Save Changes' : 'Add Designer'}
          </Button>
        }
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-xl bg-destructive/10 text-destructive px-3.5 py-2.5 text-sm">{formError}</div>
          )}
          {closeDesigner && (
            <div className="rounded-lg bg-accent/10 border border-accent/30 px-3 py-2.5 text-xs">
              <p className="text-foreground">
                You already have <span className="font-semibold">{closeDesigner.name}</span> — same brand?
              </p>
              <button
                type="button"
                onClick={() => addAliasToExisting(closeDesigner)}
                className="mt-1.5 inline-flex items-center gap-1 text-primary font-medium hover:underline"
              >
                <LinkIcon className="w-3 h-3" /> Add "{designerName.trim()}" as an alias of {closeDesigner.name}
              </button>
            </div>
          )}
          <Field label="Designer Name *">
            <input
              type="text"
              value={designerName}
              onChange={(e) => setDesignerName(e.target.value)}
              placeholder="e.g., Loro Piana, Tom Ford"
              className={inputClass}
            />
          </Field>
          <Field label="Website">
            <input
              type="url"
              value={designerWebsite}
              onChange={(e) => setDesignerWebsite(e.target.value)}
              placeholder="https://"
              className={inputClass}
            />
          </Field>
          <Field label="Aliases" hint="Other names this brand goes by. Comma-separated.">
            <input
              type="text"
              value={designerAliases}
              onChange={(e) => setDesignerAliases(e.target.value)}
              placeholder="Mr P., Mr. Porter"
              className={inputClass}
            />
          </Field>
          {dupDesigners.map((dup) => (
            <div key={dup.name} className="rounded-lg bg-accent/10 border border-accent/30 px-3 py-2.5 text-xs">
              <p className="text-foreground">
                A separate brand <span className="font-semibold">{dup.name}</span> matches this alias.
              </p>
              <button
                type="button"
                onClick={() => mergeDesignerInto(dup.name)}
                className="mt-1.5 inline-flex items-center gap-1 text-primary font-medium hover:underline"
              >
                <LinkIcon className="w-3 h-3" /> Merge {dup.name} into {designerName.trim() || 'this brand'} (moves its items here)
              </button>
            </div>
          ))}
          <Field label="Notes">
            <textarea
              value={designerNotes}
              onChange={(e) => setDesignerNotes(e.target.value)}
              placeholder="Sizing notes, preferences…"
              rows={3}
              className={cn(inputClass, 'resize-none')}
            />
          </Field>
        </div>
      </Modal>

      {/* Merge duplicate brand */}
      <Modal
        isOpen={mergeIntoD !== null}
        onClose={() => setMergeIntoD(null)}
        title={`Merge into ${mergeIntoD?.name ?? ''}`}
        footer={
          <Button fullWidth onClick={handleMergeDesigners} disabled={!mergeFromName || mergingD}>
            {mergingD ? 'Merging…' : 'Merge & Remove Duplicate'}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Pick the duplicate brand. Its items and saved sizes are re-tagged to{' '}
            <span className="font-semibold text-foreground">{mergeIntoD?.name}</span>, its name becomes an alias, and the
            duplicate is removed.
          </p>
          <Field label="Duplicate to merge in">
            <select value={mergeFromName} onChange={(e) => setMergeFromName(e.target.value)} className={inputClass}>
              <option value="">— Select a brand —</option>
              {designers
                .filter((d) => normalizeName(d.name) !== normalizeName(mergeIntoD?.name ?? ''))
                .map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
      </Modal>

      {/* Delete Designer Confirmation */}
      <Modal
        isOpen={deletingDesigner !== null}
        onClose={() => setDeletingDesigner(null)}
        title="Delete Designer"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={() => setDeletingDesigner(null)}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDeleteDesigner} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-foreground">
          Delete <span className="font-semibold">{deletingDesigner?.name}</span> from My Designers? This removes the
          designer and its saved sizing notes. Items keep their brand.
        </p>
      </Modal>

      {/* Add Size Modal */}
      <Modal
        isOpen={sizeForDesigner !== null}
        onClose={() => setSizeForDesigner(null)}
        title={`Add Size — ${sizeForDesigner?.name ?? ''}`}
        footer={
          <Button
            fullWidth
            onClick={handleAddSize}
            disabled={!garmentType.trim() || !sizeValue.trim() || saving}
          >
            {saving ? 'Saving…' : 'Save Size'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Garment Type *">
            <input
              type="text"
              value={garmentType}
              onChange={(e) => setGarmentType(e.target.value)}
              placeholder="e.g., Shirts, Suits"
              className={inputClass}
              list="designer-garment-types"
            />
            <datalist id="designer-garment-types">
              {ITEM_CATEGORIES.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          </Field>
          <Field label="Size *">
            <input
              type="text"
              value={sizeValue}
              onChange={(e) => setSizeValue(e.target.value)}
              placeholder="e.g., M, 50, 40R, 9 UK"
              className={inputClass}
            />
          </Field>
          <Field label="Fit">
            <div className="flex flex-wrap gap-2">
              {FIT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSizeFit(opt.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    sizeFit === opt.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Modal>
    </Page>
  );
}
